import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from "../../../../components";
import { Divider } from "@mui/material";
import { DashboardChart } from "../../../main/components";
import { OrdemServicoService } from "../../../../services";
import { cacheApi, responseErros } from "../../../../utils";
import { filtrosUrl } from "../../../../helpers";
import { useCacheContext } from "../../../../contexts/cache.context";

const ordemServicoService = new OrdemServicoService();

const OPCOES_VISUALIZACAO = [
  { label: "Diário", value: "dia" },
  { label: "Semanal", value: "semana" },
  { label: "Mensal", value: "mes" },
];

const MESES = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

function agruparPorPeriodo(dias, agrupamento) {
  if (agrupamento === "dia") {
    return dias.map((d) => ({
      label: d.label.slice(0, 5),
      abertas: d.abertas,
      fechadas: d.fechadas,
    }));
  }
  const mapa = new Map();
  for (const d of dias) {
    const [dd, mm, yyyy] = d.label.split("/");
    const data = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    let chave;
    let label;
    let ordem;
    if (agrupamento === "mes") {
      chave = `${yyyy}-${mm}`;
      label = `${MESES[Number(mm) - 1]}/${yyyy}`;
      ordem = new Date(Number(yyyy), Number(mm) - 1, 1);
    } else {
      const inicio = new Date(data);
      inicio.setDate(data.getDate() - data.getDay());
      const fim = new Date(inicio);
      fim.setDate(inicio.getDate() + 6);
      const di = String(inicio.getDate()).padStart(2, "0");
      const mi = String(inicio.getMonth() + 1).padStart(2, "0");
      const df = String(fim.getDate()).padStart(2, "0");
      const mf = String(fim.getMonth() + 1).padStart(2, "0");
      chave = `${inicio.getFullYear()}-${inicio.getMonth()}-${inicio.getDate()}`;
      label = `Sem ${di}/${mi} - ${df}/${mf}`;
      ordem = inicio;
    }
    let grupo = mapa.get(chave);
    if (!grupo) {
      grupo = { label, ordem, abertas: 0, fechadas: 0 };
      mapa.set(chave, grupo);
    }
    grupo.abertas += d.abertas;
    grupo.fechadas += d.fechadas;
  }
  return Array.from(mapa.values()).sort((a, b) => a.ordem - b.ordem);
}

function MonitorOsEvolucao({ filtrosBody }) {
  const { cacheDto, setCacheDto } = useCacheContext();
  const [loading, setLoading] = useState(false);
  const [agrupamento, setAgrupamento] = useState("dia");
  const [dias, setDias] = useState([]);

  useEffect(() => {
    buscarEvolucao();
  }, [filtrosBody]);

  async function buscarEvolucao() {
    setLoading(true);
    const intervalo = filtrosBody.filtros?.porIntervalo?.[0] || {};
    const labelUrl = filtrosUrl.getUrl();
    const cacheBody = {
      dto: { cacheDto, setCacheDto },
      timeMinutes: 1000,
      apiFunction:
        ordemServicoService.getRelatorioDashboardEvolucao(filtrosBody),
    };
    const result = await cacheApi(
      `${labelUrl}-${intervalo.de}-${intervalo.ate}-evolucao-os`,
      cacheBody,
    );
    if (!result.isAxiosError) {
      const payload = result.data || { categories: [], series: [] };
      const abertas =
        payload.series?.find((s) => s.name === "Abertas")?.data || [];
      const fechadas =
        payload.series?.find((s) => s.name === "Fechadas")?.data || [];
      const registros = (payload.categories || []).map((label, i) => ({
        label,
        abertas: abertas[i] || 0,
        fechadas: fechadas[i] || 0,
      }));
      setDias(registros);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const data = useMemo(() => {
    const agrupados = agruparPorPeriodo(dias, agrupamento);
    return {
      categories: agrupados.map((g) => g.label),
      series: [
        {
          name: "Abertas",
          type: "line",
          data: agrupados.map((g) => g.abertas),
        },
        {
          name: "Fechadas",
          type: "line",
          data: agrupados.map((g) => g.fechadas),
        },
      ],
    };
  }, [dias, agrupamento]);

  return (
    <Card sx={{ borderRadius: 3, border: "1px solid #f0f0f0", height: "100%" }}>
      <CardContent sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
          flexWrap="wrap"
          gap={1}
          flexShrink={0}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Evolução Operacional
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
            <Box display="flex" gap={0.5}>
              {OPCOES_VISUALIZACAO.map((opcao) => (
                <Button
                  key={opcao.value}
                  size="small"
                  color="primary"
                  variant={
                    agrupamento === opcao.value ? "contained" : "outlined"
                  }
                  onClick={() => setAgrupamento(opcao.value)}
                >
                  {opcao.label}
                </Button>
              ))}
            </Box>
            <i
              className="ph-bold ph-trend-up"
              style={{ color: "#64748b", fontSize: 20 }}
            />
          </Box>
        </Box>
        <Divider sx={{ mb: 2, flexShrink: 0 }} />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxHeight: 350,
            overflow: "hidden"
          }}
        >
          <DashboardChart
            loading={loading}
            data={data}
            heightChart={320} // Forçando altura no componente
            options={{
              chart: { 
                type: "line",
                height: 320, // Forçando altura no Highcharts
                spacing: [10, 10, 10, 10]
              },
              xAxis: {
                categories: data.categories,
                labels: { style: { fontSize: "10px" } },
              },
              yAxis: {
                title: { text: "Qtd O.S", style: { fontSize: "10px" } },
                allowDecimals: false,
                labels: { style: { fontSize: "10px" } }
              },
              legend: { 
                enabled: true,
                itemStyle: { fontSize: "10px" }
              },
              tooltip: {
                shared: true,
                useHTML: true,
                backgroundColor: "#fff",
                borderWidth: 0,
                borderRadius: 4,
                formatter: function () {
                  const rows = this.points
                    .map(
                      (p) =>
                        `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y}</b>`,
                    )
                    .join("<br/>");
                  return `<b>${this.x}</b><br/>${rows}`;
                },
              },
              plotOptions: {
                line: {
                  dataLabels: { enabled: false },
                  marker: { enabled: true, radius: 3 },
                },
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default MonitorOsEvolucao;
